import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconDownload, IconUpload } from '@tabler/icons';
import api from '../../views/services/api';

const TYPE_LABEL = {
    text: 'Texto',
    number: 'Número',
    coin: 'Moeda',
    phone: 'Telefone',
    email: 'E-mail',
    date: 'Data',
    boolean: 'Verdadeiro/Falso',
    select: 'Lista suspensa'
};

const DISCARD = '__discard__';
const CREATE_FIELD = '__create_field__';

/**
 * Dialog genérico de importação em massa via planilha - usado por todas
 * as telas com "Importar". Fluxo em 2 passos: (1) upload de QUALQUER
 * planilha, sem exigir colunas fixas; (2) mapeamento - pra cada coluna
 * detectada no arquivo, o usuário escolhe a qual campo do sistema ela
 * corresponde (ou cria um campo customizado ali mesmo, ou descarta a
 * coluna). Resolve o problema de planilhas exportadas de outros ERPs
 * com nomes de coluna que não batem com os nossos.
 */
const ImportDialog = ({ open, onClose, title, entityLabel, systemFields, fieldsEndpoint, importUrl, payloadKey, onImported }) => {
    const [step, setStep] = useState('upload'); // upload | mapping
    const [fileName, setFileName] = useState('');
    const [headers, setHeaders] = useState([]);
    const [parsedRows, setParsedRows] = useState([]);
    const [mapping, setMapping] = useState({}); // { [header]: { type: 'system'|'custom', key } }
    const [defaults, setDefaults] = useState({}); // { [header]: valor usado quando a célula vier vazia }
    const [customFields, setCustomFields] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [result, setResult] = useState(null);
    const [importing, setImporting] = useState(false);

    const [newFieldDialog, setNewFieldDialog] = useState(null); // header this field is being created for
    const [newFieldForm, setNewFieldForm] = useState({ name: '', type_field: 'text', options: '' });
    const [savingField, setSavingField] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStep('upload');
        setFileName('');
        setHeaders([]);
        setParsedRows([]);
        setMapping({});
        setDefaults({});
        setStatus({ type: '', message: '' });
        setResult(null);
        if (fieldsEndpoint) loadCustomFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const loadCustomFields = async () => {
        try {
            const res = await api.get(fieldsEndpoint);
            setCustomFields(res.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleDownloadTemplate = () => {
        const header = systemFields.map((f) => f.label).join(',');
        const blob = new Blob([`${header}\n`], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `modelo_${entityLabel}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setStatus({ type: '', message: '' });
        setFileName(file.name);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (!results.data || results.data.length === 0 || !results.meta?.fields?.length) {
                    setStatus({ type: 'error', message: 'Nenhuma coluna/linha encontrada no arquivo' });
                    return;
                }
                setHeaders(results.meta.fields);
                setParsedRows(results.data);
                setMapping({});
                setDefaults({});
                setStep('mapping');
            },
            error: () => {
                setStatus({ type: 'error', message: 'Erro ao ler o arquivo - confirme que é um CSV válido' });
            }
        });
    };

    const sampleValueFor = (header) => {
        const row = parsedRows.find((r) => (r[header] || '').toString().trim());
        return row ? row[header] : '';
    };

    const isBlankValue = (value) => value === undefined || value === null || value.toString().trim() === '';

    const blankCountFor = (header) => parsedRows.filter((r) => isBlankValue(r[header])).length;

    const handleMapChange = (header, value) => {
        if (value === CREATE_FIELD) {
            // Pré-preenche com o nome da própria coluna da planilha - evita
            // ter que redigitar o mesmo nome que já está na tela, só pra
            // confirmar (mas continua editável, caso o usuário queira um
            // nome diferente do cabeçalho original).
            setNewFieldForm({ name: header, type_field: 'text', options: '' });
            setNewFieldDialog(header);
            return;
        }
        if (value === DISCARD) {
            setMapping((prev) => ({ ...prev, [header]: { type: 'discard' } }));
            return;
        }
        const systemField = systemFields.find((f) => f.key === value);
        if (systemField) {
            setMapping((prev) => ({ ...prev, [header]: { type: 'system', key: value } }));
            return;
        }
        const customField = customFields.find((f) => f.name === value);
        if (customField) {
            setMapping((prev) => ({ ...prev, [header]: { type: 'custom', key: value } }));
        }
    };

    const handleDefaultChange = (header, value) => {
        setDefaults((prev) => ({ ...prev, [header]: value }));
    };

    const handleCreateField = async () => {
        if (!newFieldForm.name.trim()) {
            setStatus({ type: 'error', message: 'Informe o nome do campo customizado' });
            return;
        }
        setSavingField(true);
        try {
            const payload = {
                field: {
                    name: newFieldForm.name,
                    type_field: newFieldForm.type_field,
                    options: newFieldForm.type_field === 'select' ? newFieldForm.options : '',
                    required: false
                }
            };
            const createRes = await api.post(fieldsEndpoint, payload);
            const listRes = await api.get(fieldsEndpoint);
            const refreshed = listRes.data || [];
            setCustomFields(refreshed);
            const created = refreshed.find((f) => f.id === createRes.data.id);
            if (created) {
                setMapping((prev) => ({ ...prev, [newFieldDialog]: { type: 'custom', key: created.name } }));
            }
            setNewFieldDialog(null);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar campo customizado' });
        } finally {
            setSavingField(false);
        }
    };

    const buildRows = () => {
        return parsedRows.map((rawRow) => {
            const row = {};
            const fields = {};
            headers.forEach((header) => {
                const target = mapping[header];
                if (!target || target.type === 'discard') return;
                const rawValue = rawRow[header];
                const defaultValue = defaults[header];
                const value = isBlankValue(rawValue) && defaultValue ? defaultValue : rawValue;
                if (target.type === 'system') {
                    row[target.key] = value;
                } else if (target.type === 'custom') {
                    fields[target.key] = value;
                }
            });
            if (Object.keys(fields).length > 0) row.fields = fields;
            return row;
        });
    };

    const requiredSystemFields = systemFields.filter((f) => f.required);

    const missingRequiredFields = () => {
        const mappedSystemKeys = Object.values(mapping)
            .filter((target) => target.type === 'system')
            .map((target) => target.key);
        return requiredSystemFields.filter((f) => !mappedSystemKeys.includes(f.key));
    };

    const handleImportClick = () => {
        const missing = missingRequiredFields();
        if (missing.length > 0) {
            setStatus({
                type: 'error',
                message: `Falta relacionar o(s) campo(s) obrigatório(s) "${missing
                    .map((f) => f.label)
                    .join('", "')}" a alguma coluna da planilha antes de importar - selecione a coluna correspondente na tabela abaixo.`
            });
            return;
        }

        const unmapped = headers.filter((h) => !mapping[h]);
        if (unmapped.length > 0) {
            const confirmed = window.confirm(
                `As colunas "${unmapped.join('", "')}" não foram mapeadas pra nenhum campo. Deseja descartar esses dados e continuar a importação?`
            );
            if (!confirmed) return;
            setMapping((prev) => {
                const next = { ...prev };
                unmapped.forEach((h) => {
                    next[h] = { type: 'discard' };
                });
                return next;
            });
        }
        runImport();
    };

    const runImport = async () => {
        setImporting(true);
        setStatus({ type: '', message: '' });
        try {
            const rows = buildRows();
            const response = await api.post(importUrl, { [payloadKey]: rows });
            setResult(response.data);
            if (response.data.created_count > 0 && onImported) {
                onImported();
            }
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao importar arquivo' });
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setResult(null);
        onClose();
    };

    const mappedTargetValue = (header) => {
        const target = mapping[header];
        if (!target) return '';
        if (target.type === 'discard') return DISCARD;
        return target.key;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {status.message && (
                    <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                        {status.message}
                    </Alert>
                )}

                {result && (
                    <Alert severity={result.error_count > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                        {result.created_count} {entityLabel} criado(s).
                        {result.error_count > 0 && ` ${result.error_count} linha(s) com erro:`}
                        {result.errors?.length > 0 && (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {result.errors.map((e, i) => (
                                    <li key={i}>
                                        Linha {e.row}: {e.error}
                                    </li>
                                ))}
                            </Box>
                        )}
                    </Alert>
                )}

                {step === 'upload' && (
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            Envie uma planilha com os dados - pode ter qualquer coluna, em qualquer ordem. No próximo passo você
                            relaciona cada coluna da sua planilha com um campo do sistema (ou cria um campo customizado).
                        </Typography>
                        <Box>
                            <Button variant="outlined" startIcon={<IconDownload size={18} />} onClick={handleDownloadTemplate}>
                                Baixar modelo (opcional)
                            </Button>
                        </Box>
                        <Button variant="contained" component="label" startIcon={<IconUpload size={18} />} sx={{ alignSelf: 'flex-start' }}>
                            {fileName || 'Selecionar arquivo CSV'}
                            <input type="file" accept=".csv" hidden onChange={handleFileChange} />
                        </Button>
                    </Stack>
                )}

                {step === 'mapping' && (
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            {parsedRows.length} linha(s) lida(s) de <strong>{fileName}</strong>. Relacione cada coluna abaixo:
                        </Typography>
                        {requiredSystemFields.length > 0 && (
                            <Alert severity="info">
                                Os campos marcados com <strong>*</strong> são obrigatórios:{' '}
                                <strong>{requiredSystemFields.map((f) => f.label).join(', ')}</strong>. Pelo menos uma coluna da sua
                                planilha precisa ser relacionada a cada um deles em &quot;Corresponde a&quot; antes de importar.
                            </Alert>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Coluna da planilha</TableCell>
                                    <TableCell>Exemplo</TableCell>
                                    <TableCell>Corresponde a</TableCell>
                                    <TableCell>Valor padrão (se vazio)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {headers.map((header) => {
                                    const target = mapping[header];
                                    const blankCount = blankCountFor(header);
                                    const showDefault = target && target.type !== 'discard' && blankCount > 0;
                                    return (
                                        <TableRow key={header}>
                                            <TableCell>
                                                <code>{header}</code>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="text.secondary">
                                                    {sampleValueFor(header) || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    select
                                                    size="small"
                                                    fullWidth
                                                    value={mappedTargetValue(header)}
                                                    onChange={(e) => handleMapChange(header, e.target.value)}
                                                    SelectProps={{ displayEmpty: true }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Não mapeada</em>
                                                    </MenuItem>
                                                    {systemFields.map((f) => (
                                                        <MenuItem key={f.key} value={f.key}>
                                                            {f.label}
                                                            {f.required ? ' *' : ''}
                                                        </MenuItem>
                                                    ))}
                                                    {customFields.length > 0 && [
                                                        <MenuItem key="divider-custom" disabled divider>
                                                            Campos customizados
                                                        </MenuItem>,
                                                        ...customFields.map((f) => (
                                                            <MenuItem key={f.id} value={f.name}>
                                                                {f.title}
                                                            </MenuItem>
                                                        ))
                                                    ]}
                                                    {fieldsEndpoint && (
                                                        <MenuItem value={CREATE_FIELD} sx={{ fontWeight: 600 }}>
                                                            + Criar campo customizado...
                                                        </MenuItem>
                                                    )}
                                                    <MenuItem value={DISCARD}>Descartar esta coluna</MenuItem>
                                                </TextField>
                                            </TableCell>
                                            <TableCell>
                                                {showDefault ? (
                                                    <Stack spacing={0.5}>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Deixar vazio"
                                                            value={defaults[header] || ''}
                                                            onChange={(e) => handleDefaultChange(header, e.target.value)}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {blankCount} linha(s) sem valor nessa coluna
                                                        </Typography>
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Fechar</Button>
                {step === 'mapping' && (
                    <Button variant="contained" onClick={handleImportClick} disabled={importing}>
                        {importing ? 'Importando...' : 'Importar'}
                    </Button>
                )}
            </DialogActions>

            <Dialog open={Boolean(newFieldDialog)} onClose={() => setNewFieldDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Novo campo customizado</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Pra coluna <code>{newFieldDialog}</code>
                        </Typography>
                        <TextField
                            label="Nome do campo"
                            fullWidth
                            required
                            value={newFieldForm.name}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, name: e.target.value })}
                        />
                        <TextField
                            select
                            label="Tipo"
                            fullWidth
                            value={newFieldForm.type_field}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, type_field: e.target.value })}
                        >
                            {Object.entries(TYPE_LABEL).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {newFieldForm.type_field === 'select' && (
                            <TextField
                                label="Opções (separadas por vírgula)"
                                fullWidth
                                placeholder="Opção A, Opção B, Opção C"
                                value={newFieldForm.options}
                                onChange={(e) => setNewFieldForm({ ...newFieldForm, options: e.target.value })}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewFieldDialog(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleCreateField} disabled={savingField}>
                        {savingField ? 'Criando...' : 'Criar campo'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

ImportDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    entityLabel: PropTypes.string.isRequired,
    systemFields: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            required: PropTypes.bool
        })
    ).isRequired,
    fieldsEndpoint: PropTypes.string,
    importUrl: PropTypes.string.isRequired,
    payloadKey: PropTypes.string,
    onImported: PropTypes.func
};

ImportDialog.defaultProps = {
    fieldsEndpoint: null,
    payloadKey: 'rows',
    onImported: null
};

export default ImportDialog;
